import React, { useEffect, useRef, useState } from "react";
import { Avatar, ClickAwayListener, debounce, IconButton, InputBase, Paper, Typography } from "@mui/material";
import { Search, ZoomOut } from "@mui/icons-material";
import clsx from "clsx";
import { NavLink } from "react-router-dom";
import { sendExternalHit, useQuickSearch } from "../../../services/backend";
import { buildAssetUrl, buildCloudinaryFetchUrl } from "../../../utils";
import { QuickSearchResponse } from "@backend-ctrl/search.controller";
import useQuickSearchStyles from "./QuickSearch.styles";
import { useLocalStorage } from "../../../hooks";
import { storageKeys } from "@src/constants";

const SLOW_QUERY_ALERT_ms = 1000;
interface Props {
  // style?: React.CSSProperties;
  className?: string;
}
export const QuickSearch: React.FC<Props> = ({ className }) => {
  const classes = useQuickSearchStyles();
  const [searchTerm, setSearchTerm] = useState(""); // todo reducer
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(""); // todo reducer
  const [showSearch, setShowSearch] = useState(false);
  const [history, setHistory] = useLocalStorage<string[]>(storageKeys.quickSearchHistory, []);
  const setSearchTermDb = useRef(
    debounce((val: string) => {
      setDebouncedSearchTerm(val);
    }, 150)
  );

  const onInput = (val: string, quick?: boolean) => {
    if (val.length > 40) val = val.slice(0, 40);
    setSearchTerm(val);
    setSearchTermDb.current(val.toLowerCase()); // prevent re-request if same text but caps change, eg lisa -> LISA
    quick && setDebouncedSearchTerm(val.toLowerCase()); // "skip" the debounce, forcing the request now (eg, if a history term is clicked)
  };

  const {
    data: results,
    error,
    isFetching,
  } = useQuickSearch(
    { q: debouncedSearchTerm },
    {
      enabled: debouncedSearchTerm.length > 0,
      keepPreviousData: debouncedSearchTerm.length > 0,
    }
  );
  const hasNoResults = results && results.total_results === 0;

  const onSearchResultClicked = () => {
    setHistory((prev) => Array.from(new Set([searchTerm, ...prev.slice(0, 9)])));
    setShowSearch(false);
    setSearchTerm("");
    setDebouncedSearchTerm("");
  };

  const SearchBtn = (
    <IconButton
      aria-label={"Open quick search"}
      size="small"
      onClick={() => setShowSearch(!showSearch)}
      children={showSearch ? <ZoomOut /> : <Search />}
    />
  );

  const ResultsContainer: React.FC = ({ children }) => (
    <Paper elevation={4} square={true} className={classes.resultsContainer}>
      {children}
    </Paper>
  );

  useEffect(() => {
    const keyupListener = (evt: KeyboardEvent) => {
      if (showSearch || evt.key.toLowerCase() !== "s" || evt.repeat) return;
      const tagNameLower = (evt.target as any)?.tagName?.toLowerCase();
      if (/[input|textarea|select]/.test(tagNameLower)) return;
      setShowSearch(true);
    };
    window.addEventListener("keyup", keyupListener, true);
    return () => window.removeEventListener("keyup", keyupListener, true);
  }, [showSearch]);

  if (!showSearch) {
    return <div className={clsx(className, classes.root_QuickSearch)}>{SearchBtn}</div>;
  }

  return (
    <div className={clsx(className, classes.root_QuickSearch)}>
      <ClickAwayListener onClickAway={() => setShowSearch(false)}>
        <div className={classes.searchContainer}>
          {/* Search Bar */}
          <InputBase
            className={clsx(classes.inputBase, !results && history.length === 0 && classes.inputBase_NoResultsLoaded)}
            autoFocus
            placeholder={"Search for content & actors..."}
            value={searchTerm}
            onChange={(evt) => onInput(evt.target.value)}
            color="primary"
          />

          {/* Results Container */}
          {results ? (
            <ResultsContainer>
              {/* If search resposne is taking long */}
              {isFetching && <SlowQueryAlert isFetching={isFetching} />}

              {/* If no results */}
              {hasNoResults && !isFetching && (
                <Typography component="div" variant="overline" align="center" style={{ paddingTop: 8 }}>
                  {"No results found"}
                </Typography>
              )}

              {/* If smart search unavailable & no results */}
              {!results.smart && !hasNoResults && (
                <div className={classes.smartSearchUnaval}>
                  Smart Search is currently unavailable.
                  <br />
                  You may need to be more specific.
                </div>
              )}

              <LibraryItemResults onSearchResultClicked={onSearchResultClicked} library_items={results.library_items} />
              <SanneTitleItemResults onSearchResultClicked={onSearchResultClicked} items={results.sanne_items} />
              <ActorResults onSearchResultClicked={onSearchResultClicked} people={results.people} />
            </ResultsContainer>
          ) : error ? (
            <ResultsContainer>
              <Typography component="div" align="center" style={{ color: "red" }}>
                {error.message}
              </Typography>
            </ResultsContainer>
          ) : !isFetching && history.length > 0 ? (
            <ResultsContainer>
              <SearchHistory
                history={history}
                onHistoryClicked={(v) => onInput(v, true)}
                onClearHistory={() => setHistory([])}
              />
            </ResultsContainer>
          ) : null}
        </div>
      </ClickAwayListener>
      {SearchBtn}
    </div>
  );
};

type SProps = { isFetching: boolean };
const SlowQueryAlert = ({ isFetching }: SProps) => {
  const classes = useQuickSearchStyles();
  const [isTakingLong, setIsTakingLong] = useState(false);

  useEffect(() => {
    if (!isFetching) return setIsTakingLong(false);
    const timeoutID = setTimeout(() => {
      setIsTakingLong(true);
    }, SLOW_QUERY_ALERT_ms);

    return () => {
      clearTimeout(timeoutID);
    };
  }, [isFetching]);

  if (!isTakingLong) return <></>;
  return <div className={classes.slowQueryAlert}>Please wait, this is taking longer than usual...</div>;
};

interface LibraryItemResultsProps {
  library_items: QuickSearchResponse["library_items"];
  onSearchResultClicked: () => void;
}
const LibraryItemResults: React.FC<LibraryItemResultsProps> = ({ library_items, onSearchResultClicked }) => {
  const classes = useQuickSearchStyles();

  return (
    <>
      {library_items.length > 0 && <div className={classes.label}>WeepyEyes Content</div>}

      {library_items.map((item, i) => {
        const poster = buildAssetUrl(item.poster?.cloudinary_path || item.media?.cloudinary_path, "f_auto,h_150,w_100");
        const release_date = item.media?.release_date;

        return (
          <NavLink
            key={`LIBRARY_ITEM_QS_RESULT_${item.id}`}
            to={`/d/item/${item.id}${item.slug ? `-${item.slug}` : ""}`}
            onClick={() => onSearchResultClicked()}
            className={classes.link}
          >
            <div className={classes.resultItem}>
              <img className={classes.poster} src={poster} alt={""} title={item.root} loading="lazy" />
              <div className={classes.resultText_Wrapper}>
                {/* Title: X & Y - Show/Movie Name */}

                <div
                  dangerouslySetInnerHTML={{ __html: item._highlight_value || item.root }}
                  className={classes.resultText_Title}
                />

                {/* Subtitle: Show/Movie Name (year) [type] */}
                {item.media && (
                  <div className={classes.resultText_Subtitle}>
                    {item.media.title}
                    {release_date && ` (${new Date(release_date).getFullYear()})`}
                    {` [${item.media.type}]`}
                  </div>
                )}
              </div>
            </div>
          </NavLink>
        );
      })}
    </>
  );
};

interface SanneTitleItemResultsProps {
  items: QuickSearchResponse["sanne_items"];
  onSearchResultClicked: () => void;
}
const SanneTitleItemResults: React.FC<SanneTitleItemResultsProps> = ({ items, onSearchResultClicked }) => {
  const classes = useQuickSearchStyles();

  const onClick = (url: string) => {
    sendExternalHit(url);
    onSearchResultClicked();
  };

  return (
    <>
      {items.length > 0 && <div className={classes.label}>Movies / Series (By Sanne)</div>}
      {items.map((item, i) => {
        const wp_poster_url = item.wp_poster_url ? buildCloudinaryFetchUrl(item.wp_poster_url) : undefined;
        return (
          <a
            key={`SANNE_ITEM_QS_RESULT_${item.url}`}
            href={item.url}
            onClick={() => onClick(item.url)}
            onAuxClick={() => onClick(item.url)}
            className={classes.link}
          >
            <div className={classes.resultItem}>
              <img className={classes.poster} src={wp_poster_url} alt={""} title={item.title} loading="lazy" />

              <div className={classes.resultText_Wrapper}>
                {/* Title: Show/Movie Name (Year) */}
                <div
                  dangerouslySetInnerHTML={{ __html: item._highlight_value || item.title }}
                  className={classes.resultText_Title}
                />

                {/* Subtitle: English Title Show/Movie Name (year) */}
                {item.title2 && <div className={classes.resultText_Subtitle}>{item.title2}</div>}
              </div>
            </div>
          </a>
        );
      })}
    </>
  );
};

interface ActorResultsProps {
  people: QuickSearchResponse["people"];
  onSearchResultClicked: () => void;
}
const ActorResults: React.FC<ActorResultsProps> = ({ people, onSearchResultClicked }) => {
  const classes = useQuickSearchStyles();

  return (
    <>
      {people.length > 0 && <div className={classes.label}>Actors</div>}
      {people.map((person, i) => {
        const person_image = buildAssetUrl(person.cloudinary_path || person.image_path, "c_fill,g_face,h_300,w_300,f_auto");
        return (
          <NavLink
            key={`PERSON_QS_RESULT_${person.slug}`}
            to={`/person/${person.slug}`}
            onClick={() => onSearchResultClicked()}
            className={classes.link}
          >
            <div className={classes.resultItem}>
              <Avatar alt={person.name} src={person_image} />
              <div className={classes.resultText_Wrapper}>
                <div
                  dangerouslySetInnerHTML={{ __html: person._highlight_value || person.name }}
                  className={classes.resultText_Title}
                />
              </div>
            </div>
          </NavLink>
        );
      })}
    </>
  );
};

interface SearchHistoryProps {
  history: string[];
  onHistoryClicked: (val: string) => void;
  onClearHistory: () => void;
}
const SearchHistory: React.FC<SearchHistoryProps> = ({ history, onHistoryClicked, onClearHistory }) => {
  const classes = useQuickSearchStyles();
  return (
    <>
      <div className={classes.label} style={{ display: "flex", alignItems: "center" }}>
        <span style={{ flex: 1 }}>History</span>
        <div role="button" aria-label="Clear History" onClick={onClearHistory} style={{ cursor: "pointer" }}>
          [Clear]
        </div>
      </div>
      {history.map((historyValue) => (
        <div
          key={historyValue}
          role="button"
          aria-label={`Search History Term`}
          onClick={() => onHistoryClicked(historyValue)}
          className={classes.resultItem}
          style={{ cursor: "pointer" }}
        >
          {historyValue}
        </div>
      ))}
    </>
  );
};

export default QuickSearch;
